import * as React from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import type { AssignmentRuleDTO } from '@user/store/leadAssignmentRule'
import { DragLeadAssignmentRulesList } from './ReOrder'
import { useData } from './ReOrder/hooks/useData'
import { useHandlers } from './ReOrder/hooks'
import { useTheme } from '@mui/material/styles'
import { CoverageBlockDialog } from './CoverageBlockDialog'

type Props = {
  assignments: AssignmentRuleDTO[]
  onClick: () => void
  onView: (id?: number) => void
}

export function LeadAssignmentRuleList(props: Props) {
  const { assignments, onClick, onView } = props
  const { assignmentRulesReOrderTemp, setAssignmentRulesReOrderTemp } =
    useData(assignments)
  const {
    handleDragEnd,
    onBulkEnable,
    coverageBlockLabels,
    closeCoverageBlock,
  } = useHandlers({
    setAssignmentRulesReOrderTemp,
    assignmentRulesReOrderTemp,
  })

  const theme = useTheme()
  return (
    <Stack width={818}>
      <Typography
        variant="h4"
        color={theme.palette.text.primary}
        letterSpacing="0.25px"
        fontFamily="Source Sans Pro"
      >
        Assignment Rules
      </Typography>
      <Box marginLeft="auto">
        <Button
          onClick={() => onClick()}
          variant="contained"
          size="medium"
          color="primary"
          data-test-id="addAssignmentRuleBtn"
        >
          Create New Assignment Rule
        </Button>
      </Box>
      <Typography
        variant="h6"
        color={theme.palette.text.primary}
        fontFamily="Source Sans Pro Semibold"
        letterSpacing="0.15px"
      >
        Automatically Assign Leads
      </Typography>
      <Typography
        fontSize={16}
        color="rgba(14, 40, 63, 0.87)"
        letterSpacing={0.15}
        lineHeight="24px"
        fontFamily="Source Sans Pro"
      >
        Rules will be applied in the order below. If a lead matches more than
        one rule, the rule above it wins. If a lead only matches one rule, that
        rule will be applied regardless of order.
      </Typography>

      <Box flexDirection="row" display="flex" gap={1} mt={2.5}>
        <Button
          onClick={() => onBulkEnable(true)}
          variant="outlined"
          size="medium"
          color="primary"
          sx={{ color: '#19305A' }}
          data-test-id="enableAllAssignmentRulesBtn"
        >
          Enable All
        </Button>
        <Button
          onClick={() => onBulkEnable(false)}
          variant="outlined"
          size="medium"
          color="primary"
          sx={{ color: '#19305A' }}
          data-test-id="disableAllAssignmentRulesBtn"
        >
          Disable All
        </Button>
      </Box>

      <Stack gap={1.6} marginBottom={2} marginTop={1}>
        <DragLeadAssignmentRulesList
          assignments={assignments}
          onClick={onClick}
          onView={onView}
          onDragEnd={handleDragEnd}
        />
      </Stack>
      <CoverageBlockDialog
        labels={coverageBlockLabels}
        onClose={closeCoverageBlock}
      />
    </Stack>
  )
}
